import { c as _c } from "react/compiler-runtime";
import { Button, Center, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { InboxStartCtas } from '@src/components/sco/Advisors/layout/InboxStartCtas';
import { useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { TeamAdvisorPaywall } from '@src/components/sco/Advisors/shared/TeamAdvisorPaywall';
import { ConversationRow } from '@src/components/sco/Advisors/utils/conversationRows';
import { founderUserIdOf } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { DirectChatThreadAmongstUsers } from '@src/components/sco/Chat/ChatThreadAmongstUsers';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_SOURCE_TEAM } from '@src/constants/advisor-engagement-sources';

/**
 * Right pane of the inbox: the selected conversation.
 *
 * - board / founder rows → direct chat thread
 * - locked upsell rows → paywall inline (no bounce to /advisors/upgrade)
 * - Wil → never lands here; the list deep-links to `/advisors/ask` instead
 */
export const InboxChatPane = t0 => {
  const $ = _c(49);
  const {
    row,
    currentUserId,
    canAccessAdvisorTeam,
    loading,
    requestedFounderUserId,
    requestedAdvisorUserId
  } = t0;
  const paths = useAdvisorsPaths();
  const {
    canAccessPaidAdvisorTier
  } = useAdvisorsAccess();
  const paidMessagePaywall = _temp;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Center flex={1} height="full" padding={6}><Spinner size="md" /></Center>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!row && requestedAdvisorUserId && currentUserId) {
    if (!canAccessPaidAdvisorTier) {
      let t1;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = paidMessagePaywall("Upgrade to message advisors");
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      return t1;
    }
    let t1;
    if ($[2] !== currentUserId || $[3] !== requestedAdvisorUserId) {
      t1 = [currentUserId, requestedAdvisorUserId];
      $[2] = currentUserId;
      $[3] = requestedAdvisorUserId;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        hideHeader: true,
        chatMessageListProps: {
          containerStackProps: {
            minHeight: 0
          }
        }
      };
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== requestedAdvisorUserId || $[7] !== t1) {
      t3 = <Flex direction="column" flex={1} height="full" minHeight={0} width="full"><DirectChatThreadAmongstUsers key={requestedAdvisorUserId} userIds={t1} chatThreadProps={t2} /></Flex>;
      $[6] = requestedAdvisorUserId;
      $[7] = t1;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (!row) {
    const missingFounder = !!requestedFounderUserId;
    const title_0 = missingFounder ? "No conversation yet" : "Start a conversation";
    const body = missingFounder ? "Inbox opens after a handshake. Apply for this seat (or wait for them to accept) \u2014 chat is not a cold DM." : "Message an advisor from Find advisors, invite them to a board seat, or Ask Wil.";
    let t1;
    if ($[9] !== title_0) {
      t1 = <Text color="colorPalette.fg/70" fontWeight="semibold">{title_0}</Text>;
      $[9] = title_0;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== body) {
      t2 = <Text color="colorPalette.fg/60" fontSize="sm">{body}</Text>;
      $[11] = body;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t1 || $[14] !== t2) {
      t3 = <VStack gap={2}>{t1}{t2}</VStack>;
      $[13] = t1;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== missingFounder) {
      t4 = <InboxStartCtas includeOpportunities={missingFounder} />;
      $[16] = missingFounder;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t3 || $[19] !== t4) {
      t5 = <Center flex={1} height="full" padding={6}><VStack gap={4} maxWidth="sm" textAlign="center">{t3}{t4}</VStack></Center>;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  if (row.kind === "locked") {
    let t1;
    if ($[21] !== row.advisor.name) {
      t1 = <Flex align="center" flex={1} height="full" justify="center" minHeight={0} padding={6}><TeamAdvisorPaywall advisorName={row.advisor.name} variant="gate" /></Flex>;
      $[21] = row.advisor.name;
      $[22] = t1;
    } else {
      t1 = $[22];
    }
    return t1;
  }
  if (row.kind === "direct") {
    if (!currentUserId) {
      let t1;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <Center flex={1} height="full" padding={6}><Text color="colorPalette.fg/60">Sign in to message this advisor.</Text></Center>;
        $[23] = t1;
      } else {
        t1 = $[23];
      }
      return t1;
    }
    if (!canAccessPaidAdvisorTier) {
      let t1;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = paidMessagePaywall("Upgrade to message advisors");
        $[24] = t1;
      } else {
        t1 = $[24];
      }
      return t1;
    }
    let t1;
    if ($[25] !== currentUserId || $[26] !== row.userId) {
      t1 = [currentUserId, row.userId];
      $[25] = currentUserId;
      $[26] = row.userId;
      $[27] = t1;
    } else {
      t1 = $[27];
    }
    let t2;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        hideHeader: true,
        chatMessageListProps: {
          containerStackProps: {
            minHeight: 0
          }
        }
      };
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    let t3;
    if ($[29] !== row.userId || $[30] !== t1) {
      t3 = <Flex direction="column" flex={1} height="full" minHeight={0} width="full"><DirectChatThreadAmongstUsers key={row.userId} userIds={t1} chatThreadProps={t2} /></Flex>;
      $[29] = row.userId;
      $[30] = t1;
      $[31] = t3;
    } else {
      t3 = $[31];
    }
    return t3;
  }
  let t1;
  if ($[32] !== row.engagement || $[33] !== row.kind) {
    t1 = row.kind === "founder" ? founderUserIdOf(row.engagement) : row.engagement.advisorUserId || row.engagement.advisorUser?.userId;
    $[32] = row.engagement;
    $[33] = row.kind;
    $[34] = t1;
  } else {
    t1 = $[34];
  }
  const otherUserId = t1;
  const isGatedTeamAdvisor = row.kind === "board" && row.engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM && !canAccessAdvisorTeam;
  if (isGatedTeamAdvisor) {
    const t2 = row.engagement.advisorUser?.fullName;
    let t3;
    if ($[35] !== t2) {
      t3 = <Flex align="center" flex={1} height="full" justify="center" minHeight={0} padding={6}><TeamAdvisorPaywall advisorName={t2} variant="gate" /></Flex>;
      $[35] = t2;
      $[36] = t3;
    } else {
      t3 = $[36];
    }
    return t3;
  }
  if (row.kind === "board" && !canAccessPaidAdvisorTier) {
    let t2;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = paidMessagePaywall("Upgrade to message advisors");
      $[37] = t2;
    } else {
      t2 = $[37];
    }
    return t2;
  }
  if (row.kind === "founder" && !canAccessPaidAdvisorTier) {
    let t2;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = paidMessagePaywall("Upgrade to message this founder");
      $[38] = t2;
    } else {
      t2 = $[38];
    }
    return t2;
  }
  if (!otherUserId || !currentUserId) {
    let t2;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Text color="colorPalette.fg/60" textAlign="center">Could not open this conversation.</Text>;
      $[39] = t2;
    } else {
      t2 = $[39];
    }
    let t3;
    if ($[40] !== paths.inbox) {
      t3 = <Center flex={1} height="full" padding={6}><VStack gap={3}>{t2}<Button asChild={true} size="sm" variant="outline"><NextLink href={paths.inbox}>Back to inbox</NextLink></Button></VStack></Center>;
      $[40] = paths.inbox;
      $[41] = t3;
    } else {
      t3 = $[41];
    }
    return t3;
  }
  let t2;
  if ($[42] !== currentUserId || $[43] !== otherUserId) {
    t2 = [currentUserId, otherUserId];
    $[42] = currentUserId;
    $[43] = otherUserId;
    $[44] = t2;
  } else {
    t2 = $[44];
  }
  let t3;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      hideHeader: true,
      chatMessageListProps: {
        containerStackProps: {
          minHeight: 0
        }
      }
    };
    $[45] = t3;
  } else {
    t3 = $[45];
  }
  let t4;
  if ($[46] !== otherUserId || $[47] !== t2) {
    t4 = <Flex direction="column" flex={1} height="full" minHeight={0} width="full"><DirectChatThreadAmongstUsers key={otherUserId} userIds={t2} chatThreadProps={t3} /></Flex>;
    $[46] = otherUserId;
    $[47] = t2;
    $[48] = t4;
  } else {
    t4 = $[48];
  }
  return t4;
};
function _temp(title) {
  return <Flex align="center" flex={1} height="full" justify="center" minHeight={0} padding={6}><PaywalledAdvisorAction title={title} upgradeCtaLabel="Upgrade to message" variant="boxed" /></Flex>;
}